import { ReactNode } from 'react';
import { InspectionGroup, InspectionLineItem } from '@/types/model/inspection';
import { ActionAccordionRowData } from '@/types/model/actionAccordion';
import { createActionAccordionAdapter } from '@/services/util/adapters/ActionAccordionAdapter';

interface BuildInspectionActionAccordionRowsParams {
  groups: InspectionGroup[];
  groupLineItemContentMap: Record<number, Record<number, ReactNode | null>>;
  expandedGroups?: string[];
  expandedLineItems?: Record<number, string[]>;
  previousRows?: ActionAccordionRowData[];
}

const rowStateKey = (type: 'Group' | 'LineItem', id: number): string => `${type}:${id}`;

const isInspectionGroup = (source: InspectionGroup | InspectionLineItem): source is InspectionGroup => {
  return 'groupIdentifier' in source;
};

const collectPreviousState = (rows: ActionAccordionRowData[]): Record<string, { isExpanded: boolean; isSelected: boolean }> => {
  const state: Record<string, { isExpanded: boolean; isSelected: boolean }> = {};

  const visit = (currentRows: ActionAccordionRowData[]) => {
    for (const row of currentRows) {
      state[rowStateKey(row.type, row.id)] = {
        isExpanded: row.isExpanded,
        isSelected: Boolean(row.isSelected),
      };

      if (row.children && row.children.length > 0) {
        visit(row.children);
      }
    }
  };

  visit(rows);
  return state;
};

export const buildInspectionActionAccordionRows = ({
  groups,
  groupLineItemContentMap,
  expandedGroups,
  expandedLineItems,
  previousRows,
}: BuildInspectionActionAccordionRowsParams): ActionAccordionRowData[] => {
  const previousState = previousRows ? collectPreviousState(previousRows) : {};

  const adapter = createActionAccordionAdapter<InspectionGroup | InspectionLineItem>({
    getChildren: (source) => {
      if (isInspectionGroup(source)) {
        return source.lineItems ?? undefined;
      }
      return undefined;
    },
    mapRow: ({ source, index, parent }) => {
      if (isInspectionGroup(source)) {
        const saved = previousState[rowStateKey('Group', source.id)];
        const expandedByLegacy = expandedGroups?.includes(index.toString()) ?? false;

        return {
          id: source.id,
          type: 'Group',
          number: String(source.groupIdentifier),
          title: source.title,
          isExpanded: saved ? saved.isExpanded : expandedByLegacy,
          isSelected: saved?.isSelected ?? false,
        };
      }

      const lineItem = source as InspectionLineItem;
      const parentGroup = parent as InspectionGroup | undefined;
      const parentGroupId = parentGroup?.id ?? lineItem.groupId;
      const content = groupLineItemContentMap[parentGroupId]?.[lineItem.id] ?? null;
      const saved = previousState[rowStateKey('LineItem', lineItem.id)];
      const expandedByLegacy = expandedLineItems?.[parentGroupId]?.includes(lineItem.id.toString()) ?? false;

      return {
        id: lineItem.id,
        type: 'LineItem',
        number: lineItem.lineItem,
        title: lineItem.title,
        isExpanded: saved ? saved.isExpanded : expandedByLegacy,
        isExpandable: true,
        contentCount: lineItem.subItems?.length ?? 0,
        isSelected: saved?.isSelected ?? false,
        content,
      };
    },
  });

  return adapter.toRows(groups);
};
